import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { uploadToS3WithProgress, fallbackToServerUpload } from '../services/s3UploadService';
import api from '../services/authService';
import { saveSignupProgress, getSignupProgress, clearSignupProgress, SignupStep, shouldRedirectToProgress } from '../utils/signupProgress';
import { BRAND_NAME, BIG_LOGO_PATH } from '../config';

function SampleVideosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || '';
  const formDataFromState = location.state?.formData;
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [guestToken, setGuestToken] = useState('');
  const [initializing, setInitializing] = useState(false);

  const uploadsReady = Boolean(guestToken) && !initializing;

  // Check for saved progress and restore if needed
  useEffect(() => {
    const redirect = shouldRedirectToProgress('/sample-videos');
    if (redirect) {
      if (redirect.step === SignupStep.PROFILE) {
        navigate('/user-profile', { state: redirect.data });
        return;
      } else if (redirect.step === SignupStep.TERMS) {
        navigate('/terms-agreement', { state: redirect.data });
        return;
      }
    }

    // If no email in state, try to get from saved progress
    if (!emailFromState) {
      const savedProgress = getSignupProgress();
      if (savedProgress && savedProgress.data && savedProgress.data.email) {
        // Redirect to restore state
        navigate('/sample-videos', {
          state: {
            email: savedProgress.data.email,
            formData: savedProgress.data.formData,
            profileData: savedProgress.data.profileData
          },
          replace: true
        });
        return;
      }
    }

    // Save progress when component mounts
    if (emailFromState) {
      saveSignupProgress(SignupStep.SAMPLE_VIDEOS, {
        email: emailFromState,
        formData: formDataFromState || getSignupProgress()?.data?.formData || {}
      });
    }
  }, [navigate, emailFromState, formDataFromState]);

  // Get email from state or saved progress
  const displayEmail = emailFromState || getSignupProgress()?.data?.email || '';

  useEffect(() => {
    const fetchGuestToken = async () => {
      const emailToUse = displayEmail;
      if (!emailToUse) {
        setError('Email is required to upload videos. Please restart signup.');
        return;
      }

      try {
        setInitializing(true);
        const response = await api.post('/auth/sample-upload-token', { email: emailToUse });
        setGuestToken(response.data.token);
      } catch (err) {
        console.error('Error fetching guest token:', err);
        const message = err.response?.data?.message || 'Unable to prepare uploads. Please try again.';
        setError(message);
      } finally {
        setInitializing(false);
      }
    };

    if (displayEmail) {
      fetchGuestToken();
    }
  }, [displayEmail]);

  const uploadFiles = async (filesToUpload, startIndex = 0) => {
    if (filesToUpload.length === 0) {
      return;
    }

    if (!guestToken) {
      setError('Preparing upload access. Please wait a moment and try again.');
      return;
    }

    setUploading(true);
    setError('');
    const uploaded = [];

    try {
      // Upload each video to S3
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const progressIndex = startIndex + i;
        setUploadProgress(prev => ({ ...prev, [progressIndex]: 0 }));

        try {
          let fileUrl;
          try {
            // Try direct S3 upload first
            fileUrl = await uploadToS3WithProgress(
              file,
              'sample-videos',
              (progress) => {
                setUploadProgress(prev => ({ ...prev, [progressIndex]: progress }));
              },
              {
                endpoint: '/blog/s3-presigned-url/guest',
                headers: {
                  Authorization: `Bearer ${guestToken}`
                },
                extraData: {
                  email: displayEmail
                }
              }
            );
          } catch (s3Error) {
            // If CORS error, fall back to server-side upload
            if (s3Error.message === 'CORS_ERROR' || s3Error.message.includes('CORS') || s3Error.message.includes('Network error')) {
              console.log(`CORS error for ${file.name}, using server-side upload`);
              fileUrl = await fallbackToServerUpload(
                file,
                'sample-videos',
                (progress) => {
                  setUploadProgress(prev => ({ ...prev, [progressIndex]: progress }));
                },
                {
                  endpoint: '/blog/upload/guest',
                  headers: {
                    Authorization: `Bearer ${guestToken}`
                  }
                }
              );
            } else {
              throw s3Error;
            }
          }

          uploaded.push({
            fileName: file.name,
            fileUrl: fileUrl,
            fileSize: file.size
          });
        } catch (uploadError) {
          console.error(`Error uploading ${file.name}:`, uploadError);
          setError(`Failed to upload ${file.name}. Please try again.`);
          // Remove the failed file from selectedFiles
          setSelectedFiles(prev => prev.filter((f, idx) => idx !== progressIndex));
          continue; // Continue with other files
        }
      }

      // Add uploaded videos to the list
      setUploadedVideos(prev => [...prev, ...uploaded]);

      // Remove uploaded files from selectedFiles
      setSelectedFiles(prev => {
        const newFiles = [...prev];
        // Remove files that were just uploaded
        for (let i = filesToUpload.length - 1; i >= 0; i--) {
          newFiles.splice(startIndex + i, 1);
        }
        return newFiles;
      });

      // Clear progress for uploaded files
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        for (let i = 0; i < filesToUpload.length; i++) {
          delete newProgress[startIndex + i];
        }
        return newProgress;
      });
    } catch (error) {
      console.error('Error uploading videos:', error);
      setError('Failed to upload videos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (event) => {
    if (!uploadsReady || uploading) {
      setError('Upload access is still being prepared. Please wait a moment.');
      return;
    }

    const files = Array.from(event.target.files || []);
    // Filter to only video files
    const videoFiles = files.filter(file => file.type.startsWith('video/'));
    if (videoFiles.length !== files.length) {
      setError('Please select only video files');
      setTimeout(() => setError(''), 3000);
    }

    // Add files to selectedFiles and start uploading immediately
    const currentLength = selectedFiles.length;
    setSelectedFiles(prev => [...prev, ...videoFiles]);

    // Start uploading immediately
    await uploadFiles(videoFiles, currentLength);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (!uploadsReady || uploading) {
      setError('Upload access is still being prepared. Please wait a moment.');
      return;
    }

    const files = Array.from(e.dataTransfer.files || []);
    const videoFiles = files.filter(file => file.type.startsWith('video/'));
    if (videoFiles.length !== files.length) {
      setError('Please drop only video files');
      setTimeout(() => setError(''), 3000);
    }

    // Add files to selectedFiles and start uploading immediately
    const currentLength = selectedFiles.length;
    setSelectedFiles(prev => [...prev, ...videoFiles]);

    // Start uploading immediately
    await uploadFiles(videoFiles, currentLength);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleSubmit = async () => {
    if (uploadedVideos.length === 0) {
      setError('Please upload at least one video before submitting');
      return;
    }

    const emailToUse = displayEmail;
    if (!emailToUse) {
      setError('Email is required to save videos');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Save videos to user profile
      await api.post('/users/sample-videos', {
        email: emailToUse,
        videos: uploadedVideos
      });

      // Clear signup progress as signup is complete
      clearSignupProgress();

      navigate('/thank-you');
    } catch (err) {
      console.error('Error saving videos:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save videos. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const removeVideo = async (index) => {
    const videoToDelete = uploadedVideos[index];

    if (!videoToDelete || !videoToDelete.fileUrl) {
      // If no fileUrl, just remove from state
      setUploadedVideos(prev => prev.filter((_, i) => i !== index));
      return;
    }

    if (!guestToken) {
      setError('Upload access not ready. Please wait a moment and try again.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Optimistically remove from UI first
    setUploadedVideos(prev => prev.filter((_, i) => i !== index));

    try {
      // Delete from S3 and user profile using guest token
      const response = await api.delete('/users/sample-videos', {
        data: {
          email: displayEmail,
          fileUrl: videoToDelete.fileUrl
        },
        headers: {
          Authorization: `Bearer ${guestToken}`
        }
      });
      console.log('Video deleted successfully:', response.data);
      // Success - no error message needed since video is already removed from UI
    } catch (err) {
      console.error('Error deleting video:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);

      // Don't show error message - video is already removed from UI
      // Backend handles all cases gracefully (user not found, S3 errors, etc.)
      // The video is already removed from the UI, so no need to show an error
      console.log('Video deletion attempted. Video already removed from UI.');
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img
            src={BIG_LOGO_PATH}
            alt={BRAND_NAME}
            style={{ maxWidth: '140px', height: 'auto' }}
          />
        </Box>

        <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 1 }}>
          Load Sample Videos
        </Typography>
        <Typography align="center" sx={{ mb: 4, color: '#333333' }}>
          Showcase your best health product reviews and health science explanations.
        </Typography>

        {initializing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Preparing secure upload access...
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            border: '2px solid #c5c5c5',
            borderRadius: 2,
            height: 240,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4,
            position: 'relative',
            backgroundImage:
              'linear-gradient(45deg, rgba(0,0,0,0.03) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.03) 75%, rgba(0,0,0,0.03)), linear-gradient(45deg, rgba(0,0,0,0.03) 25%, transparent 25%, transparent 75%, rgba(0,0,0,0.03) 75%, rgba(0,0,0,0.03))',
            backgroundSize: '40px 40px',
            backgroundPosition: '0 0, 20px 20px',
            cursor: uploading || !uploadsReady ? 'not-allowed' : 'pointer',
            opacity: uploading || !uploadsReady ? 0.6 : 1
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <Box
            sx={{
              textAlign: 'center',
              color: '#555555',
              pointerEvents: uploading || !uploadsReady ? 'none' : 'auto'
            }}
          >
            {uploading ? (
              <CircularProgress size={48} sx={{ mb: 1 }} />
            ) : (
              <CloudUploadIcon sx={{ fontSize: 48, mb: 1 }} />
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Upload
            </Typography>
            <Typography variant="body2">
              Drag & drop or click to upload MP4 files
            </Typography>
            <input
              type="file"
              accept="video/*"
              multiple
              style={{ display: 'none' }}
              id="sample-video-input"
              onChange={handleFileChange}
              disabled={uploading || !uploadsReady}
            />
            <label htmlFor="sample-video-input">
              <Box sx={{ position: 'absolute', inset: 0 }} />
            </label>
          </Box>
        </Paper>

        {selectedFiles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Uploading Videos ({selectedFiles.length})
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {selectedFiles.map((file, index) => (
                <Grid item xs={3} key={index}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="div"
                      sx={{
                        width: '100%',
                        paddingTop: '100%',
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        backgroundColor: '#000000'
                      }}
                    >
                      <video
                        src={URL.createObjectURL(file)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        muted
                      />
                    </Box>
                    <Button
                      size="small"
                      onClick={() => removeSelectedFile(index)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        minWidth: 'auto',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.8)'
                        }
                      }}
                    >
                      ×
                    </Button>
                    {uploadProgress[index] !== undefined && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress variant="determinate" value={uploadProgress[index]} />
                        <Typography variant="caption" sx={{ mt: 0.5 }}>
                          {uploadProgress[index]}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {uploadedVideos.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Uploaded Videos ({uploadedVideos.length})
            </Typography>
            <Grid container spacing={2}>
              {uploadedVideos.map((video, index) => (
                <Grid item xs={3} key={index}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="div"
                      sx={{
                        width: '100%',
                        paddingTop: '100%',
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        backgroundColor: '#000000'
                      }}
                    >
                      <video
                        src={video.fileUrl}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        muted
                        loop
                      />
                    </Box>
                    <Button
                      size="small"
                      onClick={() => removeVideo(index)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        minWidth: 'auto',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.8)'
                        }
                      }}
                    >
                      ×
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {uploadedVideos.length === 0 && selectedFiles.length === 0 && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[1, 2, 3, 4].map((index) => (
              <Grid item xs={3} key={index}>
                <Box
                  component="div"
                  sx={{
                    width: '100%',
                    paddingTop: '100%',
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    backgroundColor: '#e0e0e0'
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || uploading || uploadedVideos.length === 0}
          sx={{
            backgroundColor: '#0b57d0',
            borderRadius: 999,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            display: 'flex',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: '#0a4ab3'
            },
            '&:disabled': {
              backgroundColor: '#cccccc'
            }
          }}
        >
          {submitting ? (
            <CircularProgress size={20} sx={{ color: '#ffffff' }} />
          ) : (
            <Typography sx={{ color: '#ffffff', fontWeight: 600 }}>
              submit
            </Typography>
          )}
        </Button>
      </Container>
    </Box>
  );
}

export default SampleVideosPage;

